import { useState, useEffect } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { axiosInstance } from '../../../utils/axios';
import { FiMenu, FiX, FiSearch, FiFilter, FiCheck } from "react-icons/fi";
import { IoMdHome } from "react-icons/io";
import { useKeycloak } from "@react-keycloak/web";

const THRESHOLD_PERCENTAGE = 60; // Backend uses 60% threshold for attentiveness

const PageTeachersAttendance = () => {
  const { classId } = useParams();
  const history = useHistory();
  const { keycloak } = useKeycloak();

  const [classDetails, setClassDetails] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [absentStudents, setAbsentStudents] = useState([]);
  const [attentiveStudents, setAttentiveStudents] = useState([]);
  const [leastAttentiveStudents, setLeastAttentiveStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Verification modal state
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyingStudent, setVerifyingStudent] = useState(null);
  const [modalStatus, setModalStatus] = useState('Present'); // 'Present' or 'Absent'
  const [modalCriteria, setModalCriteria] = useState({
    visibleInFrame: false,
    attentivenessAboveThreshold: false,
    tookPermission: false,
    technicalIssue: false,
  });

  // Manual overrides: { [studentId]: 'Present' | 'Absent' }
  const [manualOverrides, setManualOverrides] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classRes, studentsRes, attendanceRes, absentRes, attentiveRes, leastAttentiveRes] = await Promise.all([
          axiosInstance.get(`/${classId}`),
          axiosInstance.get(`teachers/virtual-class/${classId}/students`),
          axiosInstance.get(`teachers-dashboard/present-students/${classId}`),
          axiosInstance.get(`teachers-dashboard/absent-students/${classId}`).catch(() => ({ data: { body: [] } })),
          axiosInstance.get(`teachers-dashboard/attentive-students/${classId}`).catch(() => ({ data: { body: [] } })),
          axiosInstance.get(`teachers-dashboard/least-attentive-students/${classId}`).catch(() => ({ data: { body: [] } })),
        ]);

        setClassDetails(classRes.data);
        setStudents(studentsRes.data.body || []);
        setAttendanceData(attendanceRes.data.body?.[0] || null);
        setAbsentStudents(absentRes.data.body || []);
        setAttentiveStudents(attentiveRes.data.body || []);
        setLeastAttentiveStudents(leastAttentiveRes.data.body || []);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [classId]);

  const absentStudentIds = new Set(absentStudents.map(s => s.student_id));
  const attentiveMap = {};
  if (attentiveStudents.length > 0) {
    attentiveStudents.forEach(s => {
      attentiveMap[s.student_id] = s;
    });
  }
  // Build a map from least attentive students for per-student attentive_percentage
  const leastAttentiveMap = {};
  if (leastAttentiveStudents.length > 0) {
    leastAttentiveStudents.forEach(s => {
      leastAttentiveMap[s.student_id] = s;
    });
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStudentRows = () => {
    let rows = students.map((student) => {
      // Check for manual override first
      const override = manualOverrides[student.id];
      const isAbsent = override ? override === 'Absent' : absentStudentIds.has(student.id);
      const status = isAbsent ? 'Absent' : 'Present';
      const attInfo = attentiveMap[student.id];
      const leastAttInfo = leastAttentiveMap[student.id];
      const attentivePercentage = leastAttInfo?.attentive_percentage || (attInfo ? 100 : null);
      const coverage = isAbsent ? '-' : (attentivePercentage !== null ? `${Math.round(attentivePercentage)}%` : '100%');
      const absentFromClass = isAbsent ? '100%' : '0%';
      const joinedTime = isAbsent ? '-' : formatTime(classDetails?.start_time);
      const needsVerify = !override && !isAbsent && !attInfo;
      const isOverridden = !!override;

      return {
        ...student,
        status,
        coverage,
        absentFromClass,
        joinedTime,
        needsVerify,
        isAbsent,
        isOverridden,
        attentivePercentage,
      };
    });

    // Filter by search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r =>
        (r.first_name || '').toLowerCase().includes(q) ||
        (r.last_name || '').toLowerCase().includes(q)
      );
    }

    // Sort
    if (sortField) {
      rows.sort((a, b) => {
        let va = a[sortField] || '';
        let vb = b[sortField] || '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return sortDirection === 'asc' ? -1 : 1;
        if (va > vb) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return rows;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field) => (
    <span className="ml-1 text-[10px] opacity-60 cursor-pointer" onClick={() => handleSort(field)}>
      {sortField === field ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );

  // Open the verification modal for a student
  const openVerifyModal = (student) => {
    const isAbsent = absentStudentIds.has(student.id);
    const leastAttInfo = leastAttentiveMap[student.id];
    const attentivePercentage = leastAttInfo?.attentive_percentage || 0;
    const isVisibleInFrame = !isAbsent; // If not absent, they had analytics records (were visible)
    const isAboveThreshold = attentivePercentage >= THRESHOLD_PERCENTAGE;

    setVerifyingStudent(student);
    setModalStatus(isAboveThreshold || isVisibleInFrame ? 'Present' : 'Absent');
    setModalCriteria({
      visibleInFrame: isVisibleInFrame,
      attentivenessAboveThreshold: isAboveThreshold,
      tookPermission: false,
      technicalIssue: false,
    });
    setShowVerifyModal(true);
  };

  const closeVerifyModal = () => {
    setShowVerifyModal(false);
    setVerifyingStudent(null);
  };

  const handleVerifySubmit = () => {
    if (verifyingStudent) {
      setManualOverrides(prev => ({
        ...prev,
        [verifyingStudent.id]: modalStatus,
      }));
    }
    closeVerifyModal();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0D0735] text-white">
        <div className="animate-pulse">Loading attendance...</div>
      </div>
    );
  }

  const rows = getStudentRows();

  return (
    <div className="min-h-screen bg-[#0D0735] text-white">
      {/* Navbar */}
      <nav className="bg-[#0D0735] text-white p-4 shadow-md sticky top-0 z-10 border-b border-[#312871]">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Dashboard</h1>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-300">{keycloak?.tokenParsed?.given_name}</span>
            <img
              src={keycloak?.tokenParsed?.photo || "https://cdn.pixabay.com/photo/2020/07/01/12/58/icon-5359553_640.png"}
              className="h-9 w-9 rounded-full border border-[#6A4CFF] object-cover"
              alt="Profile"
            />
          </div>
        </div>
      </nav>

      <div className="p-4 lg:p-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm mb-6">
          <Link to="/teacher/home" className="text-[#6A4CFF] hover:underline flex items-center gap-1">
            <IoMdHome /> Home
          </Link>
          <span className="text-gray-500">/</span>
          <Link to={`/teacher/dashboard/${classId}`} className="text-[#6A4CFF] hover:underline">Teacher</Link>
          <span className="text-gray-500">/</span>
          <span className="text-gray-300">Attendance</span>
        </div>

        {/* Header */}
        <div className="bg-[#1a1449] rounded-xl shadow-lg border border-[#312871] overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 lg:p-6 border-b border-[#312871]">
            <h2 className="text-lg font-bold">
              Attendance <span className="text-gray-400 font-normal">- {classDetails?.subject?.title || classDetails?.title || 'Class'}</span>
            </h2>
            <div className="flex items-center gap-3 mt-3 sm:mt-0">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-[#312871]/30 border border-[#312871] text-white text-sm rounded-lg px-4 py-2 pr-9 focus:outline-none focus:border-[#6A4CFF] w-64"
                />
                <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8c88a6]" />
              </div>
              <button className="bg-[#312871]/60 hover:bg-[#312871] border border-[#312871] p-2.5 rounded-lg text-[#8c88a6] hover:text-white transition-colors">
                <FiFilter size={18} />
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="bg-[#0D0735]">
                  <th className="text-left py-4 px-4 text-[#8c88a6] font-semibold text-[11px] uppercase tracking-wider">
                    S. No
                  </th>
                  <th className="text-left py-4 px-4 text-[#8c88a6] font-semibold text-[11px] uppercase tracking-wider cursor-pointer" onClick={() => handleSort('first_name')}>
                    First Name {renderSortIcon('first_name')}
                  </th>
                  <th className="text-left py-4 px-4 text-[#8c88a6] font-semibold text-[11px] uppercase tracking-wider cursor-pointer" onClick={() => handleSort('last_name')}>
                    Last Name {renderSortIcon('last_name')}
                  </th>
                  <th className="text-left py-4 px-4 text-[#8c88a6] font-semibold text-[11px] uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')}>
                    Attendance Status {renderSortIcon('status')}
                  </th>
                  <th className="text-left py-4 px-4 text-[#8c88a6] font-semibold text-[11px] uppercase tracking-wider cursor-pointer" onClick={() => handleSort('coverage')}>
                    Coverage (Present) {renderSortIcon('coverage')}
                  </th>
                  <th className="text-left py-4 px-4 text-[#8c88a6] font-semibold text-[11px] uppercase tracking-wider">
                    Absent the Class
                  </th>
                  <th className="text-left py-4 px-4 text-[#8c88a6] font-semibold text-[11px] uppercase tracking-wider">
                    Joined the Class (Time)
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id} className="border-b border-[#312871] hover:bg-[#0D0735]/50 transition-colors">
                    <td className="py-3 px-4 text-gray-300 text-sm">{index + 1}</td>
                    <td className="py-4 px-4 text-white text-sm">{row.first_name}</td>
                    <td className="py-4 px-4 text-white text-sm">{row.last_name}</td>
                    <td className="py-4 px-4 text-sm">
                      {row.needsVerify ? (
                        <button
                          onClick={() => openVerifyModal(row)}
                          className="px-5 py-1.5 rounded-md text-xs font-semibold bg-[#6A4CFF] text-white hover:bg-[#5a3cee] transition-colors shadow-sm"
                        >
                          Verify Now
                        </button>
                      ) : row.isOverridden ? (
                        <span className="flex items-center gap-1.5">
                          <FiCheck className={row.isAbsent ? 'text-red-400' : 'text-green-400'} size={14} />
                          <span className={row.isAbsent ? 'text-red-400' : 'text-green-400'}>
                            {row.status}
                          </span>
                          <span className="text-[10px] text-[#8c88a6] ml-1">(Verified)</span>
                        </span>
                      ) : (
                        <span className={row.isAbsent ? 'text-red-400' : 'text-[#8c88a6]'}>
                          {row.status}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-white text-sm">{row.coverage}</td>
                    <td className="py-4 px-4 text-white text-sm">{row.absentFromClass}</td>
                    <td className="py-4 px-4 text-white text-sm">{row.joinedTime}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-400">No students found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Verification Modal */}
      {showVerifyModal && verifyingStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div
            className="bg-[#1a1449] rounded-2xl w-full max-w-lg shadow-2xl border border-[#312871] overflow-hidden"
            style={{ animation: 'fadeInUp 0.25s ease-out' }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-[#312871]">
              <div className="flex items-center gap-3">
                <img
                  src={verifyingStudent.photo || "https://cdn.pixabay.com/photo/2020/07/01/12/58/icon-5359553_640.png"}
                  className="h-10 w-10 rounded-full border-2 border-[#6A4CFF] object-cover"
                  alt="Student"
                />
                <h3 className="text-white font-bold text-lg">
                  {verifyingStudent.first_name} {verifyingStudent.last_name}
                </h3>
              </div>
              <button
                onClick={closeVerifyModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-6">
              {/* Topic & Time */}
              <div className="flex gap-8">
                <div>
                  <div className="flex items-center gap-1.5 text-[#8c88a6] text-xs font-semibold uppercase tracking-wider mb-1">
                    <span>📚</span> Topic
                  </div>
                  <p className="text-white font-medium">
                    {classDetails?.subject?.title || 'Class'}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-[#8c88a6] text-xs font-semibold uppercase tracking-wider mb-1">
                    <span>⏰</span> Time
                  </div>
                  <p className="text-white font-medium">
                    {formatTime(classDetails?.start_time)} - {formatTime(classDetails?.end_time)}
                  </p>
                </div>
              </div>

              {/* Overall Attendance Toggle */}
              <div>
                <div className="flex items-center gap-1.5 text-[#8c88a6] text-xs font-semibold uppercase tracking-wider mb-3">
                  <span>⚙️</span> Overall Attendance
                </div>
                <div className="flex gap-0 bg-[#0D0735] rounded-lg p-1 w-fit">
                  <button
                    onClick={() => setModalStatus('Present')}
                    className={`px-6 py-2 rounded-md text-sm font-bold transition-all duration-200 ${
                      modalStatus === 'Present'
                        ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Present
                  </button>
                  <button
                    onClick={() => setModalStatus('Absent')}
                    className={`px-6 py-2 rounded-md text-sm font-bold transition-all duration-200 ${
                      modalStatus === 'Absent'
                        ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Absent
                  </button>
                </div>
              </div>

              {/* Criteria Checklist */}
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={modalCriteria.visibleInFrame}
                    onChange={() => setModalCriteria(prev => ({ ...prev, visibleInFrame: !prev.visibleInFrame }))}
                    className="w-4 h-4 accent-[#6A4CFF] bg-transparent border-gray-600 rounded"
                  />
                  <span className={`text-sm ${modalCriteria.visibleInFrame ? 'text-white font-medium' : 'text-gray-400'}`}>
                    Student is visible in the frame
                  </span>
                  {modalCriteria.visibleInFrame && <span className="text-green-400 text-xs">✓ auto-detected</span>}
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={modalCriteria.attentivenessAboveThreshold}
                    onChange={() => setModalCriteria(prev => ({ ...prev, attentivenessAboveThreshold: !prev.attentivenessAboveThreshold }))}
                    className="w-4 h-4 accent-[#6A4CFF] bg-transparent border-gray-600 rounded"
                  />
                  <span className={`text-sm ${modalCriteria.attentivenessAboveThreshold ? 'text-white font-medium' : 'text-gray-400'}`}>
                    Attentiveness above {THRESHOLD_PERCENTAGE}% threshold
                  </span>
                  {modalCriteria.attentivenessAboveThreshold && <span className="text-green-400 text-xs">✓ auto-detected</span>}
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={modalCriteria.tookPermission}
                    onChange={() => setModalCriteria(prev => ({ ...prev, tookPermission: !prev.tookPermission }))}
                    className="w-4 h-4 accent-[#6A4CFF] bg-transparent border-gray-600 rounded"
                  />
                  <span className={`text-sm ${modalCriteria.tookPermission ? 'text-white font-medium' : 'text-gray-400'}`}>
                    Student took permission
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={modalCriteria.technicalIssue}
                    onChange={() => setModalCriteria(prev => ({ ...prev, technicalIssue: !prev.technicalIssue }))}
                    className="w-4 h-4 accent-[#6A4CFF] bg-transparent border-gray-600 rounded"
                  />
                  <span className={`text-sm ${modalCriteria.technicalIssue ? 'text-white font-medium' : 'text-gray-400'}`}>
                    Student&apos;s having technical issue
                  </span>
                </label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-4 p-5 border-t border-[#312871]">
              <button
                onClick={closeVerifyModal}
                className="flex-1 py-3 border border-[#312871] text-white rounded-xl font-bold text-sm hover:bg-[#312871]/30 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleVerifySubmit}
                className="flex-1 py-3 bg-[#6A4CFF] text-white rounded-xl font-bold text-sm hover:bg-[#5a3cee] transition-colors shadow-lg shadow-[#6A4CFF]/30"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline animation keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default PageTeachersAttendance;
